'use client';
import React, {useCallback, useMemo, useState} from 'react';
import {LocalizedHtml, useI18n} from '@udemy-v2/i18n';
import {Button} from '@udemy-v2/react-core-components';
import {Duration} from '@udemy-v2/i18n';
import {Accordion, TextSkeleton} from '@udemy-v2/react-reveal-components';
import {publishEvent, TrackImpression} from '@udemy-v2/event-tracking';

import {CurriculumSection} from './curriculum-section.react-component';
import {Curriculum as CurriculumType} from '@udemy-v2/browse-course';
import {CurriculumImpressionEvent, ClickEvent, LearningProduct} from '../../events/events';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';

import styles from './curriculum.module.scss';

const DISPLAYED_SECTIONS_COUNT = 10;

interface CurriculumProps {
    titleTypography?: 'ud-heading-lg' | 'ud-heading-xl';
    enableExpandAllControl?: boolean;
    practiceIncentiveCardsComponent?: React.ReactNode;
}

interface CurriculumContentInfoProps {
    curriculum: CurriculumType;
}

export const CurriculumContentInfo = React.memo(({curriculum}: CurriculumContentInfoProps) => {
    const {gettext, ninterpolate} = useI18n();
    const {durationInSeconds, isPracticeTestCourse} = useClpCourse();
    const {lecturesCount, practiceTestQuestionsCount} = curriculum.contentCounts || {};

    return isPracticeTestCourse ? (
        <span className={styles['content-length']}>
            {ninterpolate(
                '%(count)s question',
                '%(count)s questions',
                practiceTestQuestionsCount || 0,
                {count: practiceTestQuestionsCount || 0},
            )}
        </span>
    ) : (
        <span className={styles['content-length']}>
            {ninterpolate(
                '%(sectionCount)s section',
                '%(sectionCount)s sections',
                curriculum.sections.length,
                {
                    sectionCount: curriculum.sections.length,
                },
            )}
            {' • '}
            {ninterpolate(
                '%(lectureCount)s lecture',
                '%(lectureCount)s lectures',
                lecturesCount || 0,
                {lectureCount: lecturesCount || 0},
            )}
            {durationInSeconds && (
                <>
                    {' • '}
                    <LocalizedHtml
                        html={gettext(
                            '<span class="contentLength">%(contentLength)s</span> total length',
                        )}
                        interpolate={{
                            contentLength: (
                                <Duration
                                    numSeconds={durationInSeconds || 0}
                                    precision="minutes"
                                    presentationStyle="humanCompact"
                                />
                            ),
                        }}
                    />
                </>
            )}
        </span>
    );
});

CurriculumContentInfo.displayName = 'CurriculumContentInfo';

export const Curriculum = ({
    titleTypography = 'ud-heading-xl',
    enableExpandAllControl = true,
    practiceIncentiveCardsComponent = null,
}: CurriculumProps) => {
    const {gettext, ninterpolate} = useI18n();
    const course = useClpCourse();
    const {curriculum, id: courseId, isPracticeTestCourse} = course;
    const [areAllSectionsExpanded, setAreAllSectionsExpanded] = useState<boolean | undefined>(
        undefined,
    );
    const [displayedSectionsCount, setDisplayedSectionsCount] = useState(DISPLAYED_SECTIONS_COUNT);

    const handleShowAllSections = useCallback(() => {
        const sections = curriculum?.sections;
        if (!sections) return;
        setDisplayedSectionsCount(sections.length);
    }, [curriculum]);

    const trackImpression = useCallback(() => {
        publishEvent(
            new CurriculumImpressionEvent({
                type: LearningProduct.COURSE,
                id: parseInt(courseId, 10),
                trackingId: courseId || '',
            }),
        );
    }, [courseId]);

    const handleToggleAllSections = useCallback(() => {
        handleShowAllSections();
        setAreAllSectionsExpanded((prev) => !prev);

        publishEvent(
            new ClickEvent({
                componentName: 'curriculumRead',
                trackingId: courseId,
                relatedObjectType: LearningProduct.COURSE,
                relatedObjectId: parseInt(courseId, 10),
            }),
        );
    }, [handleShowAllSections, courseId]);

    const curriculumSections = useMemo(() => {
        if (!curriculum) {
            return (
                <TextSkeleton
                    className={styles['curriculum-loader']}
                    withTitle={true}
                    lineCountPerParagraph={3}
                    paragraphCount={16}
                />
            );
        }

        if (!curriculum.sections?.length) {
            return null;
        }

        const hiddenSectionsCount = curriculum.sections.length - displayedSectionsCount;
        const sectionComponents = curriculum.sections
            .slice(0, displayedSectionsCount)
            .map((section, index) => (
                <CurriculumSection
                    key={section.title + index}
                    arrayIndex={index}
                    title={section.title}
                    items={section.items}
                    numLectures={section?.lectureCount || 0}
                    contentLength={section?.contentLength || 0}
                    areAllSectionsExpanded={areAllSectionsExpanded}
                />
            ));

        const userCanExpandAllSections =
            enableExpandAllControl && !isPracticeTestCourse && sectionComponents.length > 1;

        return (
            <div data-purpose="course-curriculum">
                <h2
                    className={`${styles['curriculum-header']} ${titleTypography}`}
                    data-purpose="curriculum-header"
                >
                    {isPracticeTestCourse
                        ? gettext('Included in This Course')
                        : gettext('Course content')}
                </h2>
                {practiceIncentiveCardsComponent}
                <div className={styles['curriculum-sub-header']}>
                    <div className="ud-text-sm" data-testid="curriculum-stats">
                        <CurriculumContentInfo curriculum={curriculum} />
                    </div>
                    {userCanExpandAllSections ? (
                        <Button
                            data-purpose="expand-toggle"
                            size="medium"
                            udStyle="ghost"
                            aria-expanded={areAllSectionsExpanded}
                            onClick={handleToggleAllSections}
                        >
                            {areAllSectionsExpanded
                                ? gettext('Collapse all sections')
                                : gettext('Expand all sections')}
                        </Button>
                    ) : null}
                </div>
                <Accordion size="medium">{sectionComponents}</Accordion>
                {hiddenSectionsCount > 0 && (
                    <Button
                        size="medium"
                        udStyle="secondary"
                        className={styles['curriculum-show-more']}
                        onClick={handleShowAllSections}
                        data-purpose="show-more"
                    >
                        {ninterpolate('%s more section', '%s more sections', hiddenSectionsCount)}
                    </Button>
                )}
            </div>
        );
    }, [
        curriculum,
        titleTypography,
        practiceIncentiveCardsComponent,
        displayedSectionsCount,
        areAllSectionsExpanded,
        enableExpandAllControl,
        isPracticeTestCourse,
        handleToggleAllSections,
        handleShowAllSections,
        gettext,
        ninterpolate,
    ]);

    return (
        <div className="component-margin">
            <span id="objective" className="in-page-offset-anchor" />
            <TrackImpression trackFunc={trackImpression} visibilityThreshold={0.25}>
                {curriculumSections}
            </TrackImpression>
        </div>
    );
};
